import { Link } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import { Section } from "@/components/common/DetailFields";
import { ShareButtons, SemDot } from "@/components/conecta/Bits";
import { useCatalogs, catalogMap } from "@/lib/lookups";
import { fmtDate, fmtDateTime, money } from "@/lib/finance";

const Empty = ({ children }) => <p className="text-sm c-muted">{children}</p>;

const CommissionRow = ({ c, statuses }) => (
  <li data-testid="summary-commission-item" className="py-2 border-b c-line last:border-0">
    <p className="text-sm font-semibold">{c.opportunity_title}</p>
    <p className="text-xs c-text-2">
      {statuses[c.status] || c.status} · {c.amounts_hidden ? "Monto oculto" : `Esperado ${money(c.expected_amount, c.currency)} · Cobrado ${money(c.collected_amount, c.currency)}`}
    </p>
  </li>
);

export const ContactSummary = ({ s }) => {
  const cats = useCatalogs();
  const stages = catalogMap(cats, "stages");
  const statuses = catalogMap(cats, "commission_statuses");
  const c = s.contact;
  return (
    <div data-testid="contact-summary" className="space-y-3">
      <div className="c-card p-4 space-y-3">
        <Link to={`/conecta/red/${c.id}`} data-testid="summary-contact-link" className="font-heading text-xl font-extrabold hover:underline">{c.name}</Link>
        <p className="text-xs c-text-2">{[c.company, c.position, c.city].filter(Boolean).join(" · ")}</p>
        <p data-testid="summary-headline" className="text-sm">{s.headline}</p>
        <ShareButtons phone={c.phone} whatsapp={c.whatsapp} email={c.email} />
      </div>
      <Section title="Próximos pasos" testid="summary-steps">
        {s.next_steps.length === 0 ? <Empty>Sin próximos pasos.</Empty> : (
          <ul className="space-y-2">{s.next_steps.map((p) => (
            <li key={p.opportunity_id} className="text-sm">
              <Link to={`/conecta/negocios/${p.opportunity_id}`} className="font-semibold hover:underline">{p.title}</Link>
              <p className={`text-xs ${p.overdue ? "text-red-500 font-semibold" : "c-text-2"}`}>{p.overdue && <AlertTriangle className="inline w-3 h-3 mr-1" />}{p.next_step_text || "Sin texto"} · {fmtDate(p.next_step_date)}</p>
            </li>
          ))}</ul>
        )}
      </Section>
      <Section title={`Negocios (${s.opportunities.length})`} testid="summary-opportunities">
        {s.opportunities.length === 0 ? <Empty>Sin negocios con este contacto.</Empty> : (
          <ul className="space-y-2">{s.opportunities.map((o) => (
            <li key={o.id} className="flex items-center gap-2 text-sm">
              <SemDot value={o.semaphore} />
              <Link to={`/conecta/negocios/${o.id}`} className="flex-1 hover:underline">{o.title}</Link>
              <span className="text-[11px] c-muted">{stages[o.stage] || o.stage}</span>
            </li>
          ))}</ul>
        )}
      </Section>
      <Section title="Comisiones" testid="summary-commissions">
        {s.commissions.length === 0 ? <Empty>Sin comisiones.</Empty> : <ul>{s.commissions.map((x) => <CommissionRow key={x.id} c={x} statuses={statuses} />)}</ul>}
      </Section>
      <Section title="Actividad reciente" testid="summary-timeline">
        {s.timeline.length === 0 ? <Empty>Sin actividad.</Empty> : (
          <ul className="space-y-2">{s.timeline.map((e) => (
            <li key={e.id} className="text-sm"><p>{e.summary}</p><p className="text-[11px] c-muted">{e.source} · {fmtDateTime(e.created_at)}</p></li>
          ))}</ul>
        )}
      </Section>
    </div>
  );
};
